"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { BanknoteIcon, ChevronRightIcon, InfoIcon, LandmarkIcon, ScaleIcon, SmartphoneIcon, type LucideIcon } from "lucide-react"

import { fromMinorUnits, toMinorUnits } from "@ibada/shared-types/finance"

import { useLocale, useTranslations } from "@/i18n"
import { LoadingRegion, SkeletonRowsCard } from "@/shared/components/skeletons"
import { useActiveBranchSlug, useActiveWorkspaceSlug } from "@/features/auth"
import { AlivePillTone } from "@/shared/components/alive-cells"
import { FinanceAccountFormDialog } from "@/features/finance/components/accounts/finance-account-form-dialog"
import {
  FinanceSettingsNoteTile,
  FinanceSettingsSummaryCard,
  FinanceSettingsTile,
  FinanceSettingsTileGrid,
} from "@/features/finance/components/settings/finance-settings-tiles"
import { conversionDayInTimezone } from "@/features/finance/lib/conversion-date"
import { pluralKey } from "@/features/finance/lib/plural-key"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { useFinancePageState } from "@/features/finance/hooks/use-finance-page-state"
import type { OpeningAccountRow } from "@/features/finance/model/finance-close-trust-types"
import {
  FinanceAccountType,
  FinanceCategoryScope,
  FinanceDirectoryStatusFilter,
  type FinanceAccountRecord,
} from "@/features/finance/model/finance-types"
import { useOpeningBalancesStateQuery } from "@/features/finance/services/finance-close-trust-query"
import { useFinanceAccountsQuery } from "@/features/finance/services/finance-query"
import { formatCurrency } from "@/shared/lib/formatting/currency"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormSection, useFormatMoneyText } from "@/shared/components/mobile-form"
import { MobilePageSkeleton } from "@/shared/components/mobile-page-skeleton"

const ACCOUNT_ICONS: Record<FinanceAccountType, LucideIcon> = {
  [FinanceAccountType.Bank]: LandmarkIcon,
  [FinanceAccountType.Cash]: BanknoteIcon,
  [FinanceAccountType.MobileMoney]: SmartphoneIcon,
}

/** The spaced em dash (U+2014) account names carry between the institution and the account ("Equity Bank", "Compte principal"). */
const NAME_SEPARATOR = " \u2014 "

/**
 * An account's display name as a name and a muted detail. Accounts have one name field, often written
 * institution, separator, account: shown as two lines so no dash reaches the screen. The data is left as it is.
 */
export function splitAccountName(name: string): { name: string; detail: string | null } {
  const index = name.indexOf(NAME_SEPARATOR)
  if (index <= 0) return { name: name.trim(), detail: null }
  const head = name.slice(0, index).trim()
  const rest = name
    .slice(index + NAME_SEPARATOR.length)
    .split(NAME_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" · ")
  return { name: head, detail: rest || null }
}

/**
 * The calendar day of an instant in the parish's own time zone, in the app's language. The opening date is a
 * local midnight, so in a zone east of Greenwich its UTC string reads the day before: never slice it.
 */
export function formatOpeningDate(iso: string, timeZone: string | null | undefined, locale: string): string {
  const day = conversionDayInTimezone(iso, timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [year, month, date] = day.split("-").map(Number)
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, date)),
  )
}

/** Each set account's share of the total, as a percentage from 0 to 100 (0 when the total is not positive). */
export function openingShares(accounts: OpeningAccountRow[], currency: string): { total: string; shares: Map<string, number> } {
  const minors = new Map<string, bigint>()
  let total = 0n
  for (const account of accounts) {
    if (account.openingBalance === null) continue
    const minor = toMinorUnits(account.openingBalance, currency)
    minors.set(account.id, minor)
    total += minor
  }
  const shares = new Map<string, number>()
  for (const [id, minor] of minors) {
    // Basis points in bigint, then a plain number: exact for any amount, and an overdrawn account shows no bar.
    shares.set(id, total > 0n && minor > 0n ? Math.min(100, Number((minor * 10000n) / total) / 100) : 0)
  }
  return { total: fromMinorUnits(total, currency), shares }
}

/**
 * Finance → Settings → Opening balances (IBA-71, IBA-129), board Soldes d'ouverture 2.
 *
 * What each account was worth on the day Ibada Cloud became the system of record. Everything before that day
 * lives in the old books and arrives here as one number; everything on or after it is recorded transaction by
 * transaction.
 *
 * There is exactly ONE place to state or correct an opening balance: the Create/Edit account dialog. This screen
 * is a READ-ONLY summary of what those accounts currently hold; "Edit in the account" opens that same dialog. A
 * correction there posts a dated adjusting entry for the difference and the original stands forever.
 */
export function OpeningBalancesPageContent() {
  const t = useTranslations("finance.openingBalances")
  const tTypes = useTranslations("churchConfig.page.financeAccounts.accountTypes")
  const { workspaceId, activeContext, scopeBranch, parishBranch, canEditFinance } = useFinancePageState()
  const branchId = scopeBranch?.id ?? null
  const scope = activeContext === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug() ?? undefined
  const scopedBranchSlug = activeContext === "parish" ? parishBranch?.slug ?? branchSlug ?? "" : branchSlug ?? ""
  const accountsHref = buildBranchAdminUrl(scopedBranchSlug, "/finance/settings/accounts", workspaceSlug)
  // The parish clock: a placeholder branch (before access resolves) carries no time zone.
  const timeZone = scopeBranch && "timezone" in scopeBranch ? scopeBranch.timezone ?? null : null

  const ready = Boolean(workspaceId && (activeContext === "parish" || branchId))
  const query = ready && workspaceId ? { workspaceId, branchId, scope } : null

  const stateQuery = useOpeningBalancesStateQuery(query)
  // The account records behind the rows: their type (for the icon) and the record the edit dialog needs.
  const accountsQuery = useFinanceAccountsQuery(
    query
      ? { ...query, status: FinanceDirectoryStatusFilter.Active, page: 1, limit: 100, sortBy: "name", sortOrder: "asc" }
      : null,
  )
  const branchCurrency = useBranchCurrency()
  const isMobile = useIsMobile()
  const locale = useLocale()
  const formatMoneyText = useFormatMoneyText(stateQuery.data?.baseCurrency || branchCurrency)
  const [editing, setEditing] = React.useState<FinanceAccountRecord | null>(null)

  const state = stateQuery.data
  const records = React.useMemo(
    () => new Map((accountsQuery.data?.items ?? []).map((record) => [record.id, record])),
    [accountsQuery.data?.items],
  )

  if (stateQuery.isLoading) {
    return isMobile ? (
      <MobilePageSkeleton header={false} groups={[4]} />
    ) : (
      <LoadingRegion label={t("loading")}>
        <SkeletonRowsCard rows={6} leading={false} />
      </LoadingRegion>
    )
  }
  if (!state) return null

  // Opening balances are branch-scoped; use the branch currency when the state doesn't carry one.
  const currency = state.baseCurrency || branchCurrency
  const openingDay = state.openingDate ? formatOpeningDate(state.openingDate, timeZone, locale) : null

  // Phone: each account and its opening balance as a grouped list, the opening date as its title, and the two
  // notes (where to change them, corrections post an adjusting entry) as short lines under it.
  if (isMobile) {
    return (
      <div className='flex flex-col gap-5'>
        <MobileFormSection
          title={openingDay ? `${t("openingDateLabel")} · ${openingDay}` : t("account")}
          footer={
            <span className='flex flex-col gap-1.5'>
              <span>
                {t("overviewIntro")}{" "}
                <Link to={accountsHref} className='font-extrabold text-primary'>
                  {t("goToAccounts")}
                </Link>
              </span>
              <span>{t("correctionNotice")}</span>
            </span>
          }
        >
          {state.accounts.length === 0 ? (
            <p className='px-4 py-3 text-sm font-semibold text-muted-foreground'>{t("noAccountsFlat")}</p>
          ) : (
            state.accounts.map((account) => {
              const display = splitAccountName(account.name)
              return (
                <div key={account.id} className='flex min-h-[3.25rem] items-center gap-3 px-4 py-2'>
                  <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='truncate text-base font-semibold'>{display.name}</span>
                    {display.detail ? <span className='truncate text-xs text-muted-foreground'>{display.detail}</span> : null}
                  </span>
                  <span className='max-w-[45%] shrink-0 truncate text-right text-base font-extrabold tabular-nums'>
                    {account.openingBalance !== null ? formatMoneyText(String(account.openingBalance)) : t("board.notSet")}
                  </span>
                </div>
              )
            })
          )}
        </MobileFormSection>
      </div>
    )
  }

  const money = (amount: string) => formatCurrency(amount, currency, locale)
  const { total, shares } = openingShares(state.accounts, currency)
  const setCount = state.accounts.filter((account) => account.set).length
  const percent = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 })

  const editAction = (account: OpeningAccountRow) => {
    const record = records.get(account.id)
    const className = "text-primary inline-flex items-center gap-0.5 self-start text-[12.5px] font-bold hover:underline"
    // The edit dialog where the balance is stated; without the right (or the record), the accounts page.
    if (canEditFinance && record) {
      return (
        <button type='button' className={className} onClick={() => setEditing(record)}>
          {t("board.edit")}
          <ChevronRightIcon className='size-3.5' aria-hidden />
        </button>
      )
    }
    return (
      <Link to={accountsHref} className={className}>
        {t("board.edit")}
        <ChevronRightIcon className='size-3.5' aria-hidden />
      </Link>
    )
  }

  return (
    <div className='flex flex-col gap-4' data-testid='opening-balances'>
      <FinanceSettingsSummaryCard
        testId='opening-balances-summary'
        mark={<ScaleIcon className='size-[22px]' />}
        title={<span className='tabular-nums' data-testid='opening-balances-total'>{money(total)}</span>}
        subtitle={t("board.totalLabel")}
        pills={[
          {
            id: "defined",
            tone: setCount > 0 ? AlivePillTone.Success : AlivePillTone.Neutral,
            label: t(pluralKey("board.defined", setCount, { hasNone: true }), { count: setCount }),
          },
        ]}
        description={openingDay ? t("board.sentence", { date: openingDay }) : t("board.sentenceNoDate")}
        figures={openingDay ? [{ id: "date", value: openingDay, label: t("board.dateLabel") }] : []}
      />

      {state.accounts.length === 0 ? (
        <FinanceSettingsNoteTile title={t("account")}>
          {t("noAccountsFlat")}{" "}
          <Link to={accountsHref} className='text-primary font-bold hover:underline'>
            {t("goToAccounts")}
          </Link>
        </FinanceSettingsNoteTile>
      ) : (
        <FinanceSettingsTileGrid columns={3}>
          {state.accounts.map((account) => {
            const display = splitAccountName(account.name)
            const record = records.get(account.id)
            const Icon = record ? ACCOUNT_ICONS[record.type] : LandmarkIcon
            const share = shares.get(account.id) ?? 0
            const hasBalance = account.openingBalance !== null
            const typeLabel = record ? tTypes(record.type) : null
            return (
              <FinanceSettingsTile
                key={account.id}
                testId={`opening-balance-${account.id}`}
                mark={<Icon className='size-4' />}
                label={display.name}
                // The detail from the name, else the account's type (not when it repeats the name: "Caisse").
                subLabel={display.detail ?? (typeLabel && typeLabel !== display.name ? typeLabel : null)}
                value={
                  hasBalance ? (
                    <span className='tabular-nums'>{money(String(account.openingBalance))}</span>
                  ) : (
                    <span className='text-muted-foreground text-[15px] font-bold'>{t("board.notSet")}</span>
                  )
                }
              >
                {hasBalance ? (
                  <div
                    role='img'
                    aria-label={t("board.share", { percent: percent.format(share / 100) })}
                    className='bg-muted h-1.5 overflow-hidden rounded-full'
                  >
                    <div
                      className='bg-primary h-full rounded-full'
                      style={{ width: `${share}%` }}
                      data-testid={`opening-balance-share-${account.id}`}
                    />
                  </div>
                ) : null}
                {editAction(account)}
              </FinanceSettingsTile>
            )
          })}
        </FinanceSettingsTileGrid>
      )}

      <p className='text-muted-foreground flex items-start gap-2 px-1 text-[12.5px]'>
        <InfoIcon className='mt-0.5 size-3.5 shrink-0' aria-hidden />
        <span>{t("board.info")}</span>
      </p>

      <FinanceAccountFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        mode='edit'
        record={editing}
        context={activeContext === "branch" ? "branch" : "parish"}
        workspaceId={workspaceId ?? null}
        branchId={branchId}
      />
    </div>
  )
}
